import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Inbox, Send, Mail, Loader2, RefreshCw, Search, ChevronLeft, Pencil, Reply } from "lucide-react";
import ComposeEmailDialog from "../components/ComposeEmailDialog";

/**
 * Sanitize HTML email bodies before rendering with dangerouslySetInnerHTML.
 * Inbox messages can come from arbitrary external senders, so we strip:
 *   - <script> tags + event handlers (onclick, onerror, …)
 *   - <iframe>, <object>, <embed>, <form>
 *   - javascript:/data: URLs in href/src
 * This is the standard DOMPurify defaults (FORBID_TAGS / FORBID_ATTR are
 * permissive enough to keep email-style markup like <table>/<style> intact
 * which is what most marketing emails use).
 */
const sanitizeEmailHtml = (html) =>
  DOMPurify.sanitize(html || "", {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
  });

/**
 * Mailbox view — pluggable between Inbox (IMAP from investor's BYO account)
 * and Sent box (Mongo log of every outbound this investor sent through the
 * platform). One component, two routes.
 */
export default function Mailbox({ mode = "inbox" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [composeState, setComposeState] = useState(null); // null | {} | { replyTo: msg }

  const isInbox = mode === "inbox";
  const title = isInbox ? "Inbox" : "Sent box";

  const load = async () => {
    setLoading(items.length === 0);
    setRefreshing(items.length > 0);
    setError(null);
    try {
      if (isInbox) {
        const r = await api.get("/mail/inbox", { params: { limit: 50 } });
        setItems(r.data?.messages || []);
      } else {
        const r = await api.get("/mail/sent", { params: { limit: 200 } });
        setItems(r.data?.items || []);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not load mail");
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setSelected(null);
    setQuery("");
    setItems([]);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) => {
      const blob = isInbox
        ? `${m.subject || ""} ${m.from || ""} ${m.snippet || ""}`
        : `${m.subject || ""} ${(m.to || []).join(" ")} ${m.category || ""}`;
      return blob.toLowerCase().includes(q);
    });
  }, [items, query, isInbox]);

  const openSent = async (item) => {
    try {
      const r = await api.get(`/mail/sent/${item.id}`);
      setSelected(r.data);
    } catch {
      toast.error("Could not load message");
    }
  };

  return (
    <DashboardShell>
      <div className="space-y-4" data-testid={`mailbox-page-${mode}`}>
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500" style={{ fontFamily: "Outfit" }}>Email</div>
            <h1 className="text-3xl text-slate-900" style={{ fontFamily: "Outfit", fontWeight: 700 }}>{title}</h1>
            <p className="text-sm text-slate-500 mt-1">
              {isInbox
                ? "Read recent messages from your connected email account — read-only, won't mark anything as seen on the server."
                : "Every email RealtyVault Manager has sent on your behalf — searchable, audit-ready."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setComposeState({})}
              className="btn-primary-dark text-xs inline-flex items-center gap-1.5"
              data-testid={`mailbox-compose-${mode}`}
            >
              <Pencil className="w-3 h-3" /> Compose
            </button>
            <button
              onClick={load}
              disabled={refreshing}
              className="btn-outline-dark text-xs inline-flex items-center gap-1.5"
              data-testid={`mailbox-refresh-${mode}`}
            >
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Refresh
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <a href="/dashboard/mail/inbox"
             className={`text-xs font-semibold px-3 py-1.5 rounded-sm border inline-flex items-center gap-1.5 ${isInbox ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
             data-testid="mailbox-tab-inbox">
            <Inbox className="w-3 h-3" /> Inbox
          </a>
          <a href="/dashboard/mail/sent"
             className={`text-xs font-semibold px-3 py-1.5 rounded-sm border inline-flex items-center gap-1.5 ${!isInbox ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
             data-testid="mailbox-tab-sent">
            <Send className="w-3 h-3" /> Sent
          </a>
          <div className="ml-auto relative w-full sm:w-72">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            <input
              type="search"
              placeholder={isInbox ? "Search subject / sender / snippet…" : "Search subject / recipient…"}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="input-clean w-full pl-9"
              data-testid="mailbox-search"
            />
          </div>
        </div>

        {error ? (
          <div className="border border-rose-200 bg-rose-50 text-rose-900 rounded-sm p-4 text-sm" data-testid="mailbox-error">
            {error}
            {isInbox ? (
              <div className="mt-1 text-xs text-rose-700">
                Make sure your email account is connected in <a href="/dashboard/settings" className="underline font-semibold">Settings</a> with IMAP host filled in.
              </div>
            ) : null}
          </div>
        ) : null}

        {selected && !isInbox ? (
          <SentDetailView item={selected} onBack={() => setSelected(null)} />
        ) : (
          <div className="border border-slate-200 bg-white rounded-sm overflow-hidden" data-testid="mailbox-list">
            {loading ? (
              <div className="px-5 py-12 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading {title.toLowerCase()}…</div>
            ) : filtered.length === 0 ? (
              <EmptyState mode={mode} hasQuery={!!query.trim()} hasError={!!error} />
            ) : (
              <ul className="divide-y divide-slate-100">
                {filtered.map((item) => (
                  isInbox
                    ? <InboxRow key={item.uid} item={item} onOpen={() => setSelected(item)} />
                    : <SentRow key={item.id} item={item} onOpen={() => openSent(item)} />
                ))}
              </ul>
            )}
          </div>
        )}

        {selected && isInbox ? (
          <InboxDetailView item={selected} onBack={() => setSelected(null)} onReply={() => setComposeState({ replyTo: selected })} />
        ) : null}
      </div>

      <ComposeEmailDialog
        open={!!composeState}
        initial={composeState}
        onClose={() => setComposeState(null)}
        onSent={() => { setComposeState(null); if (!isInbox) load(); }}
      />
    </DashboardShell>
  );
}

function InboxRow({ item, onOpen }) {
  return (
    <li className="px-5 py-3 hover:bg-slate-50/60 cursor-pointer flex items-start gap-3" onClick={onOpen} data-testid={`inbox-row-${item.uid}`}>
      {item.unread ? <span className="w-2 h-2 rounded-full bg-sky-600 mt-2 shrink-0" aria-hidden /> : <span className="w-2 h-2 mt-2 shrink-0" aria-hidden />}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <div className={`text-sm truncate ${item.unread ? "font-bold text-slate-900" : "text-slate-700"}`}>{item.from || "Unknown sender"}</div>
          <div className="text-[11px] text-slate-400 font-mono shrink-0">{(item.date || "").slice(0, 25)}</div>
        </div>
        <div className={`text-sm truncate mt-0.5 ${item.unread ? "font-semibold text-slate-900" : "text-slate-600"}`}>{item.subject || "(no subject)"}</div>
        <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.snippet || ""}</div>
      </div>
    </li>
  );
}

function SentRow({ item, onOpen }) {
  return (
    <li className="px-5 py-3 hover:bg-slate-50/60 cursor-pointer flex items-start gap-3" onClick={onOpen} data-testid={`sent-row-${item.id}`}>
      <Mail className="w-3.5 h-3.5 mt-1 shrink-0 text-slate-400" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-sm font-semibold text-slate-900 truncate">To: {(item.to || []).join(", ")}</div>
          <div className="text-[11px] text-slate-400 font-mono shrink-0">{new Date(item.sent_at).toLocaleString()}</div>
        </div>
        <div className="text-sm text-slate-700 mt-0.5 truncate">{item.subject || "(no subject)"}</div>
        <div className="text-[10px] uppercase tracking-wider mt-0.5 inline-flex items-center gap-2 text-slate-500">
          <span className={`px-1.5 py-0.5 rounded-sm border ${badgeForStatus(item.status)}`}>{item.status}</span>
          {item.category ? <span className="text-slate-400">· {item.category.replace(/_/g, " ")}</span> : null}
          <span className="text-slate-400">· via {item.transport}</span>
        </div>
      </div>
    </li>
  );
}

function badgeForStatus(s) {
  if (s === "sent") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (s === "error") return "bg-rose-50 text-rose-800 border-rose-200";
  if (s === "mocked") return "bg-amber-50 text-amber-800 border-amber-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
}

function InboxDetailView({ item, onBack, onReply }) {
  return (
    <div className="border border-slate-200 bg-white rounded-sm" data-testid="inbox-detail">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <button onClick={onBack} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="inbox-detail-back">
          <ChevronLeft className="w-3 h-3" /> Back to list
        </button>
        <button
          onClick={onReply}
          className="ml-auto btn-primary-dark text-xs inline-flex items-center gap-1.5"
          data-testid="inbox-detail-reply"
        >
          <Reply className="w-3 h-3" /> Reply
        </button>
      </div>
      <div className="p-5">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">From</div>
        <div className="text-sm text-slate-900 mb-2">{item.from}</div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Subject</div>
        <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>{item.subject || "(no subject)"}</h3>
        <div className="text-xs text-slate-400 mb-4 font-mono">{item.date}</div>
        <div className="border border-slate-100 rounded-sm bg-white p-4 prose prose-sm max-w-none">
          {item.html ? (
            <div dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(item.html) }} />
          ) : (
            <pre className="whitespace-pre-wrap text-sm text-slate-700">{item.plain || "(empty body)"}</pre>
          )}
        </div>
      </div>
    </div>
  );
}

function SentDetailView({ item, onBack }) {
  return (
    <div className="border border-slate-200 bg-white rounded-sm" data-testid="sent-detail">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
        <button onClick={onBack} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="sent-detail-back">
          <ChevronLeft className="w-3 h-3" /> Back to list
        </button>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-2 gap-3 text-xs mb-3">
          <Meta label="To">{(item.to || []).join(", ")}</Meta>
          <Meta label="Sent at">{new Date(item.sent_at).toLocaleString()}</Meta>
          <Meta label="Status"><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${badgeForStatus(item.status)}`}>{item.status}</span></Meta>
          <Meta label="Transport">{item.transport}</Meta>
          {item.category ? <Meta label="Category">{item.category.replace(/_/g, " ")}</Meta> : null}
          {item.error ? <Meta label="Error" className="col-span-2 text-rose-700">{item.error}</Meta> : null}
        </div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Subject</div>
        <h3 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "Outfit" }}>{item.subject || "(no subject)"}</h3>
        <div className="border border-slate-100 rounded-sm bg-white p-4 prose prose-sm max-w-none">
          <div dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(item.html || "") }} />
        </div>
      </div>
    </div>
  );
}

function Meta({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500" style={{ fontFamily: "Outfit" }}>{label}</div>
      <div className="text-slate-900">{children}</div>
    </div>
  );
}

function EmptyState({ mode, hasQuery, hasError }) {
  if (hasError) return null;
  return (
    <div className="px-5 py-16 text-center" data-testid="mailbox-empty">
      <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
        {mode === "inbox" ? <Inbox className="w-6 h-6" /> : <Send className="w-6 h-6" />}
      </div>
      <div className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
        {hasQuery ? "No matches" : mode === "inbox" ? "Inbox is empty" : "No outbound mail yet"}
      </div>
      <p className="text-sm text-slate-500 mt-1">
        {hasQuery
          ? "Try a different search."
          : mode === "inbox"
            ? "Either your IMAP folder is empty, or your account hasn't received any messages yet."
            : "Every email RealtyVault Manager sends on your behalf will appear here."}
      </p>
    </div>
  );
}
